import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Headphones, Play, Pause, SkipForward } from "lucide-react";
import { api } from "../lib/api";

const absUrl = (u) => {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${u.startsWith("/") ? u : "/" + u}`;
};

const fmtDuration = (s) => {
  if (!s || Number.isNaN(s)) return "";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

/**
 * PodcastQueue
 * ------------
 * Renders the long-form 2-person podcast for the current article and — when
 * that episode finishes — auto-plays the next topically related Goldman
 * Fischer episode. Shows an "Up Next" list of 2-3 upcoming episodes so
 * listeners can preview / skip to a specific brief.
 *
 * Ranking is decided server-side (/api/articles/{id}/related-podcasts) by
 * tag overlap → category match → recency.
 */
const PodcastQueue = ({ article }) => {
  const [queue, setQueue] = useState([]);             // upcoming episodes (excluding current)
  const [nowPlayingIdx, setNowPlayingIdx] = useState(-1); // -1 = current article, 0+ = queue index
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  // Fetch related podcasts once per article
  useEffect(() => {
    if (!article?.id) return;
    let alive = true;
    api
      .get(`/articles/${article.id}/related-podcasts?limit=3`)
      .then((r) => {
        if (!alive) return;
        setQueue(
          (r.data || []).map((e) => ({
            ...e,
            podcastAudio: absUrl(e.podcastAudio),
          })),
        );
      })
      .catch(() => setQueue([]));
    return () => {
      alive = false;
    };
  }, [article?.id]);

  // The currently loaded track — either the source article, or a queued item.
  const currentTrack =
    nowPlayingIdx < 0
      ? {
          id: article.id,
          title: article.title,
          slug: article.slug,
          podcastAudio: article.podcastAudio,
          hostName: article.podcastHostVoice ? undefined : undefined, // filled by queue rows only
          guestName: undefined,
        }
      : queue[nowPlayingIdx];

  // Advance to the next track when the audio element fires "ended"
  const advance = () => {
    if (nowPlayingIdx + 1 < queue.length) {
      setNowPlayingIdx(nowPlayingIdx + 1);
      // The <audio key=...> swap will remount; start playback via effect below.
    } else {
      setPlaying(false);
    }
  };

  // Auto-play newly loaded track (only after the user has already engaged
  // with playback once — we never autoplay on first mount).
  useEffect(() => {
    if (nowPlayingIdx < 0) return; // first mount, don't autoplay
    const a = audioRef.current;
    if (!a) return;
    // Best-effort: browsers allow autoplay if audio was already unlocked by
    // a user gesture on this document, which the initial Play click provides.
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [nowPlayingIdx]);

  if (!article?.podcastAudio) return null;

  return (
    <div
      className="my-8 border border-neutral-200 bg-[#FFFFFF] p-4 md:p-5"
      data-testid="podcast-queue"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#0891B2] font-semibold">
          <Headphones className="w-3 h-3" /> Long-form podcast · Two speakers
        </div>
        <a
          href={`https://feeds.transistor.fm/vexent-wire`}
          target="_blank"
          rel="noreferrer"
          className="text-[10px] tracking-[0.25em] uppercase text-[#0891B2] hover:underline"
          data-testid="podcast-subscribe-link"
        >
          Subscribe
        </a>
      </div>

      {/* Now playing header */}
      <div className="mt-1 mb-3">
        <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">
          {nowPlayingIdx < 0 ? "Playing" : "Now playing"}
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <Link
            to={`/article/${currentTrack.slug}`}
            className="text-[15px] md:text-[17px] font-serif-display text-[#0B1F33] hover:text-[#0891B2] transition-colors"
            data-testid="podcast-now-playing-title"
          >
            {currentTrack.title}
          </Link>
          {currentTrack.podcastDurationSec ? (
            <span className="text-[11px] text-neutral-500 font-mono-tick shrink-0">
              {fmtDuration(currentTrack.podcastDurationSec)}
            </span>
          ) : null}
        </div>
        {currentTrack.hostName && currentTrack.guestName ? (
          <div className="mt-0.5 text-[11px] uppercase tracking-[0.15em] text-neutral-500">
            {currentTrack.hostName} · {currentTrack.guestName}
          </div>
        ) : null}
      </div>

      <audio
        key={currentTrack.podcastAudio}
        ref={audioRef}
        src={currentTrack.podcastAudio}
        controls
        preload="none"
        className="w-full"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={advance}
        data-testid="article-podcast-player"
      />

      {/* Up Next queue */}
      {queue.length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2 flex items-center gap-1.5">
            <SkipForward className="w-3 h-3" /> Up Next
          </div>
          <ul className="divide-y divide-neutral-200 border-t border-b border-neutral-200">
            {queue.map((ep, i) => {
              const active = i === nowPlayingIdx;
              const upcoming = i > nowPlayingIdx;
              return (
                <li
                  key={ep.id}
                  className={`py-2.5 flex items-center gap-3 ${
                    active ? "bg-white/60" : ""
                  }`}
                  data-testid={`podcast-queue-item-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => setNowPlayingIdx(i)}
                    className="w-8 h-8 rounded-full bg-[#0B1F33] hover:bg-[#B8E9F1] text-[#FFFFFF] flex items-center justify-center shrink-0 transition-colors"
                    aria-label={`Play ${ep.title}`}
                    data-testid={`podcast-play-next-${i}`}
                  >
                    {active && playing ? (
                      <Pause className="w-3.5 h-3.5" />
                    ) : (
                      <Play className="w-3.5 h-3.5 ml-0.5" />
                    )}
                  </button>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/article/${ep.slug}`}
                      className="text-[13px] md:text-[14px] font-serif-display text-[#0B1F33] hover:text-[#0891B2] transition-colors line-clamp-2"
                    >
                      {ep.title}
                    </Link>
                    <div className="mt-0.5 text-[10px] uppercase tracking-[0.15em] text-neutral-500 truncate">
                      {ep.hostName} · {ep.guestName}
                      {ep.podcastDurationSec
                        ? ` · ${fmtDuration(ep.podcastDurationSec)}`
                        : ""}
                    </div>
                  </div>
                  {upcoming ? (
                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 shrink-0 hidden sm:block">
                      #{i - Math.max(nowPlayingIdx, -1)}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <div className="mt-2 text-[11px] text-neutral-500">
            When this episode ends, the next brief plays automatically.
          </div>
        </div>
      )}
    </div>
  );
};

export default PodcastQueue;
